import {
  CondensedButton,
  Flex,
  Heading,
  IconAddLine,
  IconButton,
  IconEditLine,
  IconTrashLine,
  Text,
  Tooltip,
} from "@instructure/ui";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { ActionService } from "../../services/ActionService";
import { ExternalService } from "../../services/ExternalService";
import { ReflectionService } from "../../services/ReflectionService";
import { ReflectionTemplateService } from "../../services/ReflectionTemplateService";
import { UserDetailContext } from "../../utils/Context";
import { formatToReadableTime } from "../../utils/utils";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo/DialogYesNo";
import { fetchHelperTextForTemplate } from "../Reflection/utils";
import { ActionEditPanel } from "./ActionEditPanel";

export const Actions = ({ contextId, externalUserName }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const [actions, setActions] = useState([]);
  const [editingAction, setEditingAction] = useState(null);
  const [loadActions, setLoadActions] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [title, setTitle] = useState(t("actions.addAction"));
  const [actionToDelete, setActionToDelete] = useState(null);
  const [reflectionList, setReflectionList] = useState([]);
  const [reflectionTemplates, setReflectionTemplates] = useState([]);

  const actionDraft = {
    action_type: {},
    author: !externalUserName ? userDetails.name : externalUserName,
    reflections: [],
  };

  useEffect(() => {
    async function fetchReflectionsAndActions() {
      try {
        setLoadActions(true);

        const getActions = externalUserName
          ? ExternalService.getExternalActionsForContext
          : ActionService.getActionsForContext;

        const actions = await getActions(contextId);
        setActions(actions);

        let templates;
        if (!externalUserName) {
          templates = await ReflectionTemplateService.getTemplates();
          templates = templates.map((template) => ({
            ...template,
            label: template.name,
          }));
          templates.unshift({ id: "0", label: "None" });
          setReflectionTemplates(templates);
        }

        const getReflections = externalUserName
          ? ExternalService.getExternalReflectionsForContext
          : ReflectionService.getReflectionsForContext;

        const reflections = await getReflections(
          contextId,
          userDetails.canvas_id
        );

        const filteredReflections = [];

        for (const reflection of reflections) {
          if (templates) {
            if (reflection.from_template) {
              if (
                !filteredReflections.some((r) => r.ddl_id === reflection.ddl_id)
              ) {
                const template = templates.find(
                  (t) => t.id === reflection.template_id
                );
                const groupedReflections = reflections.filter(
                  (r) => r.ddl_id === reflection.ddl_id
                );

                const helperTexts = await fetchHelperTextForTemplate(
                  template.sections
                );

                const templateReflection = {
                  actions: reflection.actions,
                  ddl_id: reflection.ddl_id,
                  from_template: true,
                  template_id: reflection.template_id,
                  is_draft: reflection.is_draft,
                  metadata: reflection.metadata,
                  name: reflection.name,
                  timestamp: reflection.timestamp,
                  sections: template.sections.map((section, i) => ({
                    id: section.id,
                    content: groupedReflections.find(
                      (r) => r.section_id === section.id
                    ).content,
                    header_question: section.header_question,
                    helper_text: helperTexts[i].content,
                  })),
                };
                filteredReflections.push(templateReflection);
              }
            } else {
              filteredReflections.push(reflection);
            }
          } else {
            filteredReflections.push(reflection);
          }
        }

        setReflectionList(filteredReflections);
      } catch {
        showNotification(
          t("notifications.general.fetchError", { obj: t("actions.actions") }),
          ERROR
        );
      } finally {
        setLoadActions(false);
      }
    }
    fetchReflectionsAndActions();
  }, [contextId, showNotification, externalUserName, userDetails.canvas_id]);

  const handleDeletedActionReflections = async (reflections, action) => {
    for (const reflection of reflections) {
      reflection.actions = reflection.actions.filter(
        (actionId) => actionId !== action.id
      );
      await handleRemoveActionFromReflection(reflection);
    }
  };

  const handleRemoveActionFromReflection = async (reflection) => {
    const updateReflection = externalUserName
      ? ExternalService.addExternalReflectionForContext
      : ReflectionService.addReflectionForContext;

    if (reflection.from_template) {
      const promiseList = reflection.sections.map((section) =>
        updateReflection({
          reflectionId: reflection.ddl_id,
          sectionId: section.id,
          templateId: reflection.template_id,
          contextId: contextId,
          author: reflection.metadata.author,
          actions: reflection.actions,
          htmlContent: section.content,
        })
      );

      await Promise.all(promiseList);

      setReflectionList((prevList) =>
        prevList.filter((item) => item.ddl_id !== reflection.ddl_id)
      );
    } else {
      await updateReflection({
        reflectionId: reflection.ddl_id,
        contextId: contextId,
        author: reflection.metadata.author,
        actions: reflection.actions,
        htmlContent: reflection.content,
      });

      setReflectionList((prevList) =>
        prevList.filter((item) => item.ddl_id !== reflection.ddl_id)
      );
    }
  };

  const actionUpdated = (updatedAction) => {
    setActions((prevList) =>
      prevList.map((action) =>
        action.id === updatedAction.id ? updatedAction : action
      )
    );
  };

  const actionCreated = (newAction) => {
    setActions((prevList) => [newAction, ...prevList]);
  };

  async function handleRemoveAction() {
    try {
      const deleteAction = externalUserName
        ? ExternalService.deleteExternalAction
        : ActionService.deleteAction;

      await deleteAction(actionToDelete.id);

      const reflectionsToUpdate = reflectionList.filter((reflection) =>
        reflection.actions.includes(actionToDelete.id)
      );
      if (reflectionsToUpdate.length > 0) {
        await handleDeletedActionReflections(
          reflectionsToUpdate,
          actionToDelete
        );
      }

      setActions((prevList) =>
        prevList.filter((action) => action.id !== actionToDelete.id)
      );
      setActionToDelete(null);
    } catch {
      showNotification(
        t("notifications.general.deleteError", { obj: t("actions.action") }),
        ERROR
      );
    }
  }

  return (
    <>
      <Flex alignItems="center" margin="xx-small 0 small 0" gap="small">
        <Flex.Item>
          <Heading as="h3" level="h4">
            {t("actions.actions")}
          </Heading>
        </Flex.Item>
        <Flex.Item>
          <CondensedButton
            size="small"
            renderIcon={IconAddLine}
            onClick={() => {
              setEditingAction({ ...actionDraft });
              setTitle(t("actions.addAction"));
              setOpenModal(true);
            }}
            interaction={loadActions ? "disabled" : "enabled"}
          >
            {t("actions.addAction")}
          </CondensedButton>
        </Flex.Item>
      </Flex>
      {loadActions ? (
        <Skeleton
          count={3}
          style={{ height: 64, borderRadius: 5, marginBottom: 10 }}
        />
      ) : !actions || actions.length === 0 ? (
        <Heading level="h5" as="h4">
          {t("actions.noActions")}
        </Heading>
      ) : (
        actions.map((action, key) => (
          <Flex key={key} margin="0 0 medium 0">
            <Flex.Item shouldShrink shouldGrow>
              <Text as="p" size="small">
                {action.text}
              </Text>
              <Text as="p" fontStyle="italic" size="small">
                {action.author}
                {" - "}
                {formatToReadableTime(new Date(action.timestamp + "Z"))}
              </Text>
            </Flex.Item>
            <Flex.Item margin="0 small 0 0" align="start">
              <Tooltip
                color="primary-inverse"
                renderTip={t("actions.editAction")}
              >
                <IconButton
                  screenReaderLabel={t("actions.editAction")}
                  renderIcon={IconEditLine}
                  size="small"
                  onClick={() => {
                    setTitle(t("actions.editAction"));
                    setEditingAction({ ...action });
                    setOpenModal(true);
                  }}
                />
              </Tooltip>
            </Flex.Item>
            <Flex.Item align="start">
              <Tooltip
                color="primary-inverse"
                renderTip={t("actions.deleteAction")}
              >
                <IconButton
                  screenReaderLabel={t("actions.deleteAction")}
                  renderIcon={IconTrashLine}
                  size="small"
                  onClick={() => setActionToDelete(action)}
                />
              </Tooltip>
            </Flex.Item>
          </Flex>
        ))
      )}
      <ActionEditPanel
        title={title}
        contextId={contextId}
        action={editingAction}
        openModal={openModal}
        setOpenModal={setOpenModal}
        handleActionUpdated={actionUpdated}
        handleActionCreated={actionCreated}
        attachedReflections={[]}
        external={!!externalUserName}
        handleClose={() => setEditingAction(null)}
        reflectionList={reflectionList.filter((r) => r.is_draft === false)}
        setReflectionList={setReflectionList}
        reflectionTemplates={reflectionTemplates}
      />
      <DialogYesNo
        message={t("actions.deleteConfirm")}
        openStatus={!!actionToDelete}
        onClickYes={handleRemoveAction}
        onClickNo={() => setActionToDelete(null)}
      />
    </>
  );
};
